import {
    TICK,
    TRANSITIONTIME_MED,
    TT_SHORT,
} from "@sonic/CustomComponents/headless/components/Navigation/Transitions/config";
import { Button } from "@sonic/CustomComponents/headless/elements/Button/Button";
import { breakpointsForMediaQuery } from "@sonic/CustomComponents/headless/utils/Breakpoints";
import React, { Dispatch, SetStateAction, useEffect } from "react";
import { CSSTransition } from "react-transition-group";
import styled from "styled-components";

interface ownProps {
    hasBackdrop?: boolean;
    children?: React.ReactNode;
    setIsOpen: Dispatch<SetStateAction<boolean>>;
    title?: string;
    hasLogo?: boolean;
    hasCancelButton?: boolean;
    cancelButtonTitle?: string;
    hasContinueButton?: boolean;
    continueButtonTitle?: string;
    action?: () => void;
    declineAction?: () => void;
    showCloseButton?: boolean;
    isCenteredOnMobile?: boolean;
}

const ModalBackground = styled.div`
    background-color: rgba(0, 0, 0, 0.2);
    width: calc(100vw + 6px);
    height: 100vh;
    top: 50%;
    left: 49.9%;
    transform: translate(-50%, -50%);
    position: fixed;
    z-index: 99999;
`;

const ModalOverlay = styled.div`
    background-image: url("/staticContent/img/Modal_background.webp");
    background-repeat: no-repeat;
    background-size: cover;
    background-position: center;
    width: calc(100vw + 6px);
    height: 100vh;
    top: 50%;
    left: 49.9%;
    transform: translate(calc(-50% + 1px), -50%);
    position: fixed;
    z-index: 99999;
`;

const ModalWrapper = styled.div<{ isCenteredOnMobile?: boolean }>`
    border: solid 1px rgba(151, 151, 151, 0.2);
    border-radius: 12px;
    box-shadow: 0 0 96px 0 rgba(0, 0, 0, 0.3);
    padding: 28px 24px;
    background-color: #fff;
    width: 342px;
    height: auto;
    min-height: 330px;
    position: fixed;
    z-index: 99999;
    color: #000;
    display: flex;
    flex-direction: column;
    gap: 18px;
    text-align: left;
    font-size: 16px;

    ${({ isCenteredOnMobile }) =>
        !isCenteredOnMobile &&
        `
        animation: create-box-mobile ${TRANSITIONTIME_MED}ms forwards;
        transform: translate(-50%, -50%);   
        left: 50%;
        bottom: -165px;

        @media ${breakpointsForMediaQuery.md} {        
            bottom: inherit;
            top: 50%;
            left: 50%;
            animation: create-box ${TRANSITIONTIME_MED}ms;
        }
    `}

    ${({ isCenteredOnMobile }) =>
        isCenteredOnMobile &&
        `
        animation: create-box-mobile-centered ${TRANSITIONTIME_MED}ms;
        transform: translate(-50%, 50%);   
        left: 50%;
        bottom: 50%;

        @media ${breakpointsForMediaQuery.md} {        
            animation: create-box-centered ${TRANSITIONTIME_MED}ms;
        }
    `}

    @keyframes create-box {
        from {
            transform: translate(-50%, -50%) scale(0);
        }
        to {
            transform: translate(-50%, -50%) scale(1);
        }
    }

    @keyframes create-box-mobile {
        from {
            bottom: -100%;
            transform: translate(-50%, -50%);
        }
        to {
            bottom: -150px;
            transform: translate(-50%, -50%);
        }
    }

    @keyframes create-box-mobile-centered {
        from {
            bottom: -100%;
            transform: translate(-50%, -50%);
        }
        to {
            bottom: 50%;
            transform: translate(-50%, 50%);
        }
    }

    @keyframes create-box-centered {
        from {
            opacity: 0;
            transform: translate(-50%, 50%) scale(0);
        }
        to {
            opacity: 1;
            transform: translate(-50%, 50%) scale(1);
        }
    }

    .close-button {
        position: absolute;
        right: 17px;
        top: 17px;
    }

    &.fade-in {
        opacity: 0.48;
        transition: opacity ${TRANSITIONTIME_MED}ms;

        > * {
            transition: opacity ${TRANSITIONTIME_MED}ms;
            opacity: 0;
        }
    }
    &.fade-in-enter {
        opacity: 0.48;
    }
    &.fade-in-enter-active {
        opacity: 1;
    }
    &.fade-in-enter-done {
        opacity: 1;
        > * {
            opacity: 1;
        }
    }
    &.fade-in-exit {
        opacity: 1;
    }
    &.fade-in-exit-active {
        opacity: 0.48;
    }
`;

const ModalHeader = styled.div`
    display: grid;

    img {
        justify-self: center;
        width: auto;
        height: auto;
    }

    svg {
        justify-self: center;
        width: auto;
        height: auto;
    }
`;

const ModalContent = styled.div`
    line-height: 20px;

    p {
        font-size: 1rem;
        line-height: 20px;
        color: #000;
        font-family: "Pancetta W05 Regular", sans-serif;
        margin: 0;
    }

    a {
        color: #e30613;
        cursor: pointer;

        &:hover {
            color: #e30613;
        }
    }
`;

const ModalTitle = styled.h3`
    font-size: 32px;
    font-family: "Pancetta W05 Bold", sans-serif;
    line-height: normal;
`;

const ModalActions = styled.div`
    display: flex;
    flex-direction: column;
    gap: 6px;
    margin-top: 6px;
`;

const GenericModal = ({
    hasBackdrop,
    children,
    setIsOpen,
    hasCancelButton,
    cancelButtonTitle,
    title,
    hasLogo,
    continueButtonTitle,
    hasContinueButton,
    action,
    declineAction,
    showCloseButton = false,
    isCenteredOnMobile = false,
}: ownProps) => {
    const [animateContent, setAnimateContent] = React.useState(false);
    const hasHeader = hasLogo || title;
    const handleDecline = () => {
        setIsOpen(false);
        if (declineAction) {
            declineAction();
        }
    };

    useEffect(() => {
        setTimeout(() => {
            setAnimateContent(true);
        }, TICK);
    }, []);

    return (
        <>
            {hasBackdrop && <ModalBackground onClick={() => handleDecline()}></ModalBackground>}
            {!hasBackdrop && <ModalOverlay></ModalOverlay>}
            <CSSTransition timeout={TRANSITIONTIME_MED} in={animateContent} classNames="fade-in">
                <ModalWrapper className="fade-in" isCenteredOnMobile={isCenteredOnMobile}>
                    {showCloseButton && (
                        <Button
                            onClick={() => setIsOpen(false)}
                            classNames="close-button"
                            showTitle={false}
                            title="close"
                            icon={{ iconName: "close", iconColor: "#000", iconHeight: 12, iconWidth: 12 }}
                        ></Button>
                    )}
                    {hasHeader && (
                        <ModalHeader>
                            {hasLogo ? (
                                <svg width="134" height="32" viewBox="0 0 134 32" xmlns="http://www.w3.org/2000/svg">
                                    <g fill="none" fillRule="evenodd">
                                        <path
                                            d="M15.83 11.533c2.42 0 4.382 1.977 4.382 4.415 0 8.794-7.1 15.95-15.83 15.95C1.963 31.897 0 29.92 0 27.481c0-2.438 1.962-4.415 4.383-4.415 3.895 0 7.064-3.193 7.064-7.119 0-2.438 1.963-4.415 4.383-4.415zM15.83 0c2.42 0 4.383 1.977 4.383 4.415 0 2.439-1.963 4.416-4.383 4.416-3.897 0-7.065 3.193-7.065 7.117 0 2.438-1.962 4.415-4.382 4.415S0 18.386 0 15.948C0 7.154 7.101 0 15.83 0zm8.208 16.022c0 8.794 7.101 15.95 15.83 15.95s15.83-7.156 15.83-15.95S48.597.074 39.868.074s-15.83 7.154-15.83 15.948m8.765 0c0-3.924 3.169-7.118 7.065-7.118 3.895 0 7.065 3.194 7.065 7.118 0 3.926-3.17 7.119-7.065 7.119-3.896 0-7.065-3.193-7.065-7.12m76.888-11.275c-6.173 6.217-6.173 16.335-.002 22.553 6.173 6.217 16.215 6.217 22.388 0a4.44 4.44 0 0 0 .001-6.244 4.36 4.36 0 0 0-6.197 0 7.04 7.04 0 0 1-9.994 0c-2.753-2.775-2.753-7.29.002-10.065a7.036 7.036 0 0 1 9.992-.002 4.36 4.36 0 0 0 6.197 0 4.44 4.44 0 0 0-.001-6.245c-6.173-6.217-16.215-6.216-22.386.003m-17.162-.174v22.863c0 2.439 1.961 4.416 4.381 4.416 2.422 0 4.384-1.977 4.384-4.416V4.572c0-2.439-1.962-4.416-4.384-4.416-2.42 0-4.38 1.977-4.38 4.416M64.097.1c-2.42 0-4.382 1.977-4.382 4.415v23.07c0 2.438 1.962 4.415 4.382 4.415s4.383-1.977 4.383-4.415V8.93h3.933c3.895 0 7.065 3.192 7.065 7.118v11.537c0 2.438 1.962 4.415 4.382 4.415s4.383-1.977 4.383-4.415V16.048C88.244 7.254 81.143.1 72.414.1h-8.316z"
                                            fill="#000"
                                        />
                                        <path
                                            d="M47.036 16.022c0 3.99-3.209 7.222-7.168 7.222-3.96 0-7.169-3.233-7.169-7.222 0-3.989 3.21-7.222 7.169-7.222 3.96 0 7.168 3.233 7.168 7.222"
                                            fill="#E30613"
                                        />
                                    </g>
                                </svg>
                            ) : (
                                <ModalTitle>{title}</ModalTitle>
                            )}
                        </ModalHeader>
                    )}
                    <ModalContent className="markdown_custom">{children}</ModalContent>
                    <ModalActions>
                        {hasContinueButton && action && (
                            <Button
                                type="primaryRoundButton"
                                title={continueButtonTitle ?? "Continue"}
                                onClick={action}
                                icon={{
                                    iconWidth: 15,
                                    iconHeight: 15,
                                    iconName: "arrowRight",
                                }}
                            />
                        )}
                        {hasCancelButton && (
                            <Button
                                type="secondaryRoundButton"
                                title={cancelButtonTitle ?? "Decline"}
                                onClick={() => handleDecline()}
                            />
                        )}
                    </ModalActions>
                </ModalWrapper>
            </CSSTransition>
        </>
    );
};

export default GenericModal;
